<!-- Copyright (C) 2024 Skylark Drones -->
<template>
  <div>
    <home-nav-bar-btn
      class="mb-2 mb-md-0"
      btn-label="What's New"
      :to="{ name: 'Changelog' }"
    />
    <home-nav-bar-btn
      class="mb-2 mb-md-0"
      btn-label="Industry News"
      :to="{ name: 'NewsOverview' }"
    />
    <home-nav-bar-btn
      btn-label="Data Check"
      class="mb-2 mb-md-0"
      @click="navigateToVerificationPage"
    />
    <home-nav-bar-btn
      v-if="DISPLAY_PLANS"
      btn-label="Pricing"
      :to="{ name: 'PlansDetailsPage' }"
    />
    <v-divider v-if="$vuetify.breakpoint.smAndDown" class="my-4" />
    <v-btn
      rounded
      color="primary"
      text
      height="39"
      class="ml-md-5 mb-2 mb-md-0"
      :href="androidM3EAppLink"
      :block="$vuetify.breakpoint.smAndDown"
    >
      <v-icon left>mdi-download-outline</v-icon>
      Get DMO
    </v-btn>
    <v-btn
      rounded
      color="primary"
      depressed
      outlined
      height="39"
      class="ml-md-5 mb-2 mb-md-0"
      :block="$vuetify.breakpoint.smAndDown"
      @click="openLoginDialog"
    >
      Login
    </v-btn>
    <v-btn
      rounded
      color="primary"
      depressed
      height="39"
      class="ml-md-5 mb-2 mb-md-0"
      :block="$vuetify.breakpoint.smAndDown"
      @click="openRegisterDialog"
    >
      Register
    </v-btn>
    <login-card
      v-if="showLoginDialog"
      :google-auth="this.$router.currentRoute.query"
      @close="showLoginDialog = false"
      @forgot-password="openResetPasswordDialog"
    />
    <reset-password
      v-if="showResetPasswordDialog"
      has-password
      @close="showResetPasswordDialog = false"
      @password-reset="closeResetPasswordDialog"
    />
    <register-card
      v-if="showRegisterDialog"
      :google-auth="this.$router.currentRoute.query"
      @close="showRegisterDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import * as Sentry from '@sentry/browser'

import { DISPLAY_PLANS } from '@/constants/plans'
import {
  page,
  source,
  errorType
} from '@/utils/analytics/abstraction/eventEnums'
import { findAccount } from '@/services/UserService'
import HomeNavBarBtn from '@/components/homepage/HomeNavBarBtn.vue'

const LoginCard = () => import('@/components/homepage/LoginCard')
const RegisterCard = () => import('@/components/homepage/RegisterCard')
const ResetPassword = () => import('@/components/homepage/ResetPassword')

export default {
  name: 'HomeNavBarLinks',

  components: { HomeNavBarBtn, LoginCard, RegisterCard, ResetPassword },

  data() {
    return {
      DISPLAY_PLANS,
      showLoginDialog: false,
      showRegisterDialog: false,
      showResetPasswordDialog: false,
      isCheckingAccount: false
    }
  },

  computed: {
    ...mapGetters('brand', ['androidM3EAppLink'])
  },

  mounted() {
    if ('id_token' in this.$router.currentRoute.query) {
      this.getIdentifierAccount(this.$router.currentRoute.query.user_email)
    }

    if ('error' in this.$router.currentRoute.query) {
      this.$analytics.trackError(
        page.HomePage,
        errorType.GOOGLE_AUTH,
        this.$router.currentRoute.query.error
      )
      this.errorHandler(this.$router.currentRoute.query.error)
    }
  },

  methods: {
    ...mapMutations(['updateNotificationPayload']),
    navigateToVerificationPage() {
      this.$analytics.trackDataVerificationView(source.DATA_CHECK_CARD)
      this.$router.push({ name: 'DroneDataVerification' })
    },
    async getIdentifierAccount(identifier) {
      try {
        this.isCheckingAccount = true
        const resp = await findAccount('email', identifier)

        this.showLoginDialog = resp.data.user_found
        this.showRegisterDialog = !resp.data.user_found
      } catch (error) {
        console.error(error)

        this.updateNotificationPayload({
          code: 400,
          message: 'Failed to login/register using google',
          timeout: 5000
        })

        Sentry.captureException(error, {
          tags: { source: 'auth', operation: 'google-oauth-account-lookup' }
        })
      } finally {
        this.isCheckingAccount = false
      }
    },
    openLoginDialog() {
      this.$analytics.trackLoginView(page.HomePage)
      this.showRegisterDialog = false
      this.showLoginDialog = true
    },
    openRegisterDialog() {
      this.$analytics.trackSignupView(page.HomePage)
      this.showLoginDialog = false
      this.showRegisterDialog = true
    },
    openResetPasswordDialog() {
      this.$analytics.trackForgotPasswordView()
      this.showLoginDialog = false
      this.showResetPasswordDialog = true
    },
    closeResetPasswordDialog() {
      this.showResetPasswordDialog = false
      this.showLoginDialog = true
    },
    async errorHandler(e) {
      Sentry.setTags({
        source: 'auth',
        operation: 'after_authorization'
      })
      switch (e) {
        case 'popup_blocked_by_browser':
          Sentry.addBreadcrumb({
            message: `${e}: Popup blocked`,
            level: 'log'
          })
          this.updateNotificationPayload({
            code: 400,
            message:
              'Your browser may have blocked popups that are required to continue with Google. ' +
              'Please unblock them and refresh the page.',
            timeout: -1
          })
          break
        case 'cookie_disabled_by_browser':
          Sentry.addBreadcrumb({
            message: `${e}: Cookies disabled`,
            level: 'info'
          })
          this.updateNotificationPayload({
            code: 400,
            message:
              'Your browser may have disabled cookies that are required to sign in with Google. ' +
              'Please enable them and refresh the page.',
            timeout: -1
          })
          break
        case 'google_auth_obj_initialize_error':
          Sentry.addBreadcrumb({
            message: `${e}: Google Sign-in service error`,
            level: 'info'
          })
          this.updateNotificationPayload({
            code: 400,
            message:
              'Error in the Google Sign-in service! Please try again later.',
            timeout: -1
          })
          break
        case 'popup_closed_by_user':
          Sentry.addBreadcrumb({
            message: 'Popup closed by user',
            level: 'info'
          })
          // https://developers.google.com/identity/sign-in/web/reference#error_codes_2
          break
        case 'TransportError':
          Sentry.captureException(`${e}: Fail to validate credentials`, {
            tags: { source: 'auth', operation: 'after-authorization' }
          })
          this.updateNotificationPayload({
            code: 400,
            message:
              'Something went wrong when validating your credentials! Please try again later',
            timeout: -1
          })
          break
        default:
          this.updateNotificationPayload({
            code: 400,
            message: 'Error signing in. Please try again later.',
            timeout: 5000
          })
          Sentry.captureException(e, {
            tags: { source: 'auth', operation: 'after-authorization' }
          })
          await this.$router.push({ name: 'Forbidden' })
          break
      }
    }
  }
}
</script>
